/**
 * MyVoicesPage
 *
 * Displays a list of user's cloned voices and default pre-made voices
 * with options to manage settings, preview, and delete voices.
 */

import { useReducer, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PostWriterSubmenuMemo } from '@/components/post-writer/PostWriterSubmenu';
import { VoiceList } from '@/components/voices/VoiceList';
import { DefaultVoiceList } from '@/components/voices/DefaultVoiceList';
import { VoiceSettingsEditor } from '@/components/voices/VoiceSettingsEditor';
import { DeleteVoiceDialog } from '@/components/voices/DeleteVoiceDialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useClonedVoices } from '@/hooks/useClonedVoices';
import { useDefaultVoices } from '@/hooks/useDefaultVoices';
import { Mic, Loader2, Plus, ChevronDown, Globe } from 'lucide-react';
import type { Id } from '../../convex/_generated/dataModel';
import { VOICE_CONSTANTS } from '@/@types/voices';

type VoicePageState = {
  editingVoiceId: Id<'clonedVoices'> | null;
  deletingVoiceId: Id<'clonedVoices'> | null;
  refreshingVoiceId: Id<'clonedVoices'> | null;
  isDefaultVoicesOpen: boolean;
  isClonedVoicesOpen: boolean;
};

type VoicePageAction =
  | { type: 'SET_EDITING'; payload: Id<'clonedVoices'> | null }
  | { type: 'SET_DELETING'; payload: Id<'clonedVoices'> | null }
  | { type: 'SET_REFRESHING'; payload: Id<'clonedVoices'> | null }
  | { type: 'TOGGLE_DEFAULT_VOICES'; payload: boolean }
  | { type: 'TOGGLE_CLONED_VOICES'; payload: boolean };

const initialVoicePageState: VoicePageState = {
  editingVoiceId: null,
  deletingVoiceId: null,
  refreshingVoiceId: null,
  isDefaultVoicesOpen: true,
  isClonedVoicesOpen: true,
};

function voicePageReducer(state: VoicePageState, action: VoicePageAction): VoicePageState {
  switch (action.type) {
    case 'SET_EDITING':
      return { ...state, editingVoiceId: action.payload };
    case 'SET_DELETING':
      return { ...state, deletingVoiceId: action.payload };
    case 'SET_REFRESHING':
      return { ...state, refreshingVoiceId: action.payload };
    case 'TOGGLE_DEFAULT_VOICES':
      return { ...state, isDefaultVoicesOpen: action.payload };
    case 'TOGGLE_CLONED_VOICES':
      return { ...state, isClonedVoicesOpen: action.payload };
  }
}

export const MyVoicesPage = () => {
  const navigate = useNavigate();
  const [ui, dispatch] = useReducer(voicePageReducer, initialVoicePageState);

  // Default voices hook
  const { voices: defaultVoices } = useDefaultVoices();

  // Hook for CRUD operations
  const {
    activeVoices,
    voiceCount,
    isLoading,
    isDeleting,
    isUpdatingSettings,
    canCreateVoice,
    deleteVoice,
    updateSettings,
    refreshPreview,
    getVoiceById,
  } = useClonedVoices({
    onDeleteSuccess: () => dispatch({ type: 'SET_DELETING', payload: null }),
  });

  // Navigate to clone page
  const handleCloneVoice = useCallback(() => {
    navigate('/voices/clone');
  }, [navigate]);

  // Delete voice handler
  const handleDeleteConfirm = useCallback(async () => {
    if (!ui.deletingVoiceId) return;
    await deleteVoice(ui.deletingVoiceId);
  }, [ui.deletingVoiceId, deleteVoice]);

  // Save settings handler
  const handleSaveSettings = useCallback(
    async (settings: Parameters<typeof updateSettings>[1]) => {
      if (!ui.editingVoiceId) return;
      const success = await updateSettings(ui.editingVoiceId, settings);
      if (success) {
        dispatch({ type: 'SET_EDITING', payload: null });
      }
    },
    [ui.editingVoiceId, updateSettings]
  );

  // Refresh preview handler
  const handleRefreshPreview = useCallback(
    async (voiceId: Id<'clonedVoices'>) => {
      dispatch({ type: 'SET_REFRESHING', payload: voiceId });
      await refreshPreview(voiceId);
      dispatch({ type: 'SET_REFRESHING', payload: null });
    },
    [refreshPreview]
  );

  // Find voices for dialogs
  const editingVoice = ui.editingVoiceId ? getVoiceById(ui.editingVoiceId) : undefined;
  const deletingVoice = ui.deletingVoiceId ? getVoiceById(ui.deletingVoiceId) : undefined;

  return {
    submenu: <PostWriterSubmenuMemo />,
    mainContent: (
      <div className="h-full bg-background">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">My Voices</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage your cloned voices for TTS generation.{' '}
                <span className="text-muted-foreground/70">
                  ({voiceCount}/{VOICE_CONSTANTS.MAX_VOICES_PER_USER} voices)
                </span>
              </p>
            </div>
            <Button onClick={handleCloneVoice} disabled={!canCreateVoice} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Clone Voice
            </Button>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
            </div>
          ) : (
            <div className="space-y-8">
              {/* Cloned Voices Section */}
              <Collapsible
                open={ui.isClonedVoicesOpen}
                onOpenChange={open => dispatch({ type: 'TOGGLE_CLONED_VOICES', payload: open })}
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center justify-between w-full p-3 rounded-lg bg-blue-950/30 hover:bg-blue-950/50 light:bg-blue-50 light:hover:bg-blue-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Mic className="h-5 w-5 text-blue-400 light:text-blue-600" />
                      <span className="font-medium text-foreground">My Cloned Voices</span>
                      <span className="text-sm text-muted-foreground">
                        ({voiceCount}/{VOICE_CONSTANTS.MAX_VOICES_PER_USER})
                      </span>
                    </div>
                    <ChevronDown
                      className={`h-5 w-5 text-muted-foreground transition-transform ${
                        ui.isClonedVoicesOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-4">
                  {activeVoices.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed border-border rounded-lg">
                      <Mic className="h-12 w-12 text-muted-foreground/50 mb-4" />
                      <h3 className="text-lg font-medium text-foreground">No cloned voices yet</h3>
                      <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                        Clone your own voice for a personalized TTS experience.
                      </p>
                      <Button onClick={handleCloneVoice} className="mt-4" size="sm">
                        <Plus className="h-4 w-4 mr-2" />
                        Clone Your First Voice
                      </Button>
                    </div>
                  ) : (
                    <VoiceList
                      voices={activeVoices}
                      isLoading={isLoading}
                      onDeleteVoice={voiceId =>
                        dispatch({ type: 'SET_DELETING', payload: voiceId })
                      }
                      onEditSettings={voiceId =>
                        dispatch({ type: 'SET_EDITING', payload: voiceId })
                      }
                      onRefreshPreview={handleRefreshPreview}
                      refreshingVoiceId={ui.refreshingVoiceId}
                    />
                  )}
                </CollapsibleContent>
              </Collapsible>

              {/* Default Voices Section */}
              <Collapsible
                open={ui.isDefaultVoicesOpen}
                onOpenChange={open => dispatch({ type: 'TOGGLE_DEFAULT_VOICES', payload: open })}
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center justify-between w-full p-3 rounded-lg bg-purple-950/30 hover:bg-purple-950/50 light:bg-purple-50 light:hover:bg-purple-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Globe className="h-5 w-5 text-purple-400 light:text-purple-600" />
                      <span className="font-medium text-foreground">Pre-made Voices</span>
                      <span className="text-sm text-muted-foreground">
                        ({defaultVoices.length} available)
                      </span>
                    </div>
                    <ChevronDown
                      className={`h-5 w-5 text-muted-foreground transition-transform ${
                        ui.isDefaultVoicesOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-4">
                  <DefaultVoiceList voices={defaultVoices} />
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}

          {/* Edit Settings Dialog */}
          <Dialog
            open={ui.editingVoiceId !== null}
            onOpenChange={open => !open && dispatch({ type: 'SET_EDITING', payload: null })}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Voice Settings - {editingVoice?.name}</DialogTitle>
              </DialogHeader>
              {editingVoice && (
                <VoiceSettingsEditor
                  key={editingVoice._id}
                  voiceId={editingVoice._id}
                  settings={editingVoice.voiceSettings}
                  onSave={handleSaveSettings}
                  isSaving={isUpdatingSettings}
                />
              )}
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <DeleteVoiceDialog
            voiceName={deletingVoice?.name || ''}
            isOpen={ui.deletingVoiceId !== null}
            onClose={() => dispatch({ type: 'SET_DELETING', payload: null })}
            onConfirm={handleDeleteConfirm}
            isDeleting={isDeleting}
          />
        </div>
      </div>
    ),
  };
};
